import axios from 'axios';
import CustomSelect from '@/components/form/custom-select';
import type {ICustomSelect} from '@/components/form/custom-select/types';
import type {INewsListCard} from '@/components/news-list/types';
import type {IObserver} from '@/common/scripts/types/observer';
import Observer from '@/common/scripts/observer';
import templateCards from './templates/cards.twig';
import templateEmpty from './templates/empty.twig';
import {Utils} from '@/common/scripts/utils';

const ACTIVE_CLASS: string = 'is-active';
const HIDE_CLASS: string = 'is-hide';

const observer: IObserver = new Observer();

class NewsList {
    private wrapper: HTMLElement;
    private action: string;
    private tabs: HTMLElement[];
    private contentWrapper: HTMLElement;
    private selectWrapper: HTMLElement;
    private moreButton: HTMLElement;
    private page: number = 1;
    private category: string | null = null;
    private periodSelectWrapper: HTMLElement;
    private periodSelect: ICustomSelect;
    private count: number = 9;
    private mobileCount: number = 6;

    /**
     * Инициализация компонента
     * @param {HTMLElement} wrapper - обёртка
     */
    init(wrapper: HTMLElement): void {
        this.wrapper = wrapper;
        this.action = this.wrapper.dataset['action'] || '';

        if (this.wrapper.dataset['count']) {
            this.count = Number(this.wrapper.dataset['count']);
        }

        if (this.wrapper.dataset['mobileCount']) {
            this.mobileCount = Number(this.wrapper.dataset['mobileCount']);
        }

        this.setElements();
        this.initSelect();
        this.subscribe();
        this.bindEvents();
    }

    /**
     * Получение зависимых элементов
     * @private
     */
    private setElements(): void {
        this.tabs = [...this.wrapper.querySelectorAll('.j-news-list-tab')] as HTMLElement[];
        this.contentWrapper = this.wrapper.querySelector('.j-news-list-items') as HTMLElement;
        this.selectWrapper = this.wrapper.querySelector('.j-news-list-select-wrapper') as HTMLElement;
        this.moreButton = this.wrapper.querySelector('.j-news-list-more') as HTMLElement;
        this.periodSelectWrapper = this.wrapper.querySelector('.j-news-list-select') as HTMLElement;
    }

    /**
     * Инициализация селекта
     * @private
     */
    private initSelect(): void {
        if (!this.periodSelectWrapper) {
            return;
        }

        const select = new CustomSelect({
            element: this.periodSelectWrapper
        });

        select.init();

        this.periodSelect = select;
    }

    /**
     * Навешивает обработчики событий
     * @private
     */
    private bindEvents(): void {
        this.tabs.forEach((tab: HTMLElement) => {
            tab.addEventListener('click', () => {
                this.clearTabsActive();

                tab.classList.add(ACTIVE_CLASS);

                this.changeTab(tab.dataset['tab'] || null);
            });
        });

        this.moreButton?.addEventListener('click', () => {
            this.loadMore();
        });
    }

    /**
     * Очащает активность табов
     * @private
     */
    private clearTabsActive(): void {
        this.tabs.forEach((tab: HTMLElement) => {
            tab.classList.remove(ACTIVE_CLASS);
        });
    }

    /**
     * Подгружает ещё новости/акции
     * @private
     */
    private loadMore(): void {
        this.page = this.page + 1;

        this.fetchItems('next');
    }

    /**
     * Событие при смене таба
     * Загружает новости или акции или всё, в зависимости от типа
     * @param {string|null} category - выбранный тип
     * @private
     */
    private changeTab(category: string | null): void {
        this.category = category;
        this.page = 1;

        this.fetchItems('tab');
    }

    /**
     * Подписка на обновление других модулей
     * @private
     */
    private subscribe(): void {
        observer.subscribe('custom-select:change', (element: HTMLElement) => {
            if (this.periodSelect?.element === element) {
                this.page = 1;

                this.fetchItems('period');
            }
        });
    }

    /**
     * Запрашивает новости/акции
     * @param {string} type - выбранный тип
     * @private
     */
    private fetchItems(type: string = ''): void {
        const form = this.getData(type);

        axios.post(this.action, form)
            .then(({data}: any) => {
                if (!data.success) {
                    throw new Error('Ошибка получения данных');
                }

                if (type === 'next') {
                    this.addCards(data?.data?.cards || []);
                } else {
                    this.insertCards(data?.data?.cards || []);
                    this.insertSelect(data?.data?.dates || []);
                }

                // eslint-disable-next-line
                const isLastPage = String(data?.data?.pagination?.count) === String(data?.data?.pagination?.current_page);

                this.changeMoreButton(isLastPage || data?.data?.pagination?.total_elements === 0);
            })
            .catch((error: any) => {
                console.error('Error: ', error);
            });
    }

    /**
     * Добавляет карточки подгруженных новостей/акций к уже имеющимся
     * @param {Array} cards - массив карточек
     * @private
     */
    private addCards(cards: INewsListCard[]): void {
        Utils.insetContent(this.contentWrapper, templateCards({cards}));
    }

    /**
     * Очищает список карточек и догружает новые
     * @param {Array} cards - массив карточек
     * @private
     */
    private insertCards(cards: INewsListCard[]): void {
        Utils.clearHtml(this.contentWrapper);
        Utils.insetContent(this.contentWrapper, templateCards({cards}));

        if (!cards.length) {
            Utils.insetContent(this.contentWrapper, templateEmpty());
        }
    }

    /**
     * Очищает селект и загружает новый
     * @param {Array} dates - массив дат
     * @private
     */
    private insertSelect(dates: any[]): void {
        const inputs = [...this.selectWrapper.querySelectorAll('input')] as HTMLInputElement[];

        dates.forEach((date: { name: string, value: string, selected: boolean }) => {
            const dateInput = inputs.find((input: HTMLInputElement) => {
                return input.name === date.name && input.value === date.value;
            });

            if (dateInput) {
                dateInput.checked = date.selected;
            }
        });

        this.periodSelect.update();
    }

    /**
     * Скрывает/раскрывает кнопку подгрузки новостей/акций
     * @param {boolean} hide - скрываем ли кнопку
     * @private
     */
    private changeMoreButton(hide: boolean): void {
        if (hide) {
            this.moreButton.classList.add(HIDE_CLASS);
        } else {
            this.moreButton.classList.remove(HIDE_CLASS);
        }
    }

    /**
     * Формирует данные для запроса карточек
     * @param {string} type - тип подгружаемых карточек
     * @return {FormData} - данные в формате FormData
     * @private
     */
    private getData(type: string): FormData {
        const checked = [...this.periodSelectWrapper.querySelectorAll('input:checked')] as HTMLInputElement[];
        const limit = Utils.isMobile() ? this.mobileCount : this.count;

        const form = new FormData();

        form.append('type', type);
        form.append('limit', `${limit}`);
        form.append('page', `${this.page}`);

        if (this.category) {
            form.append('category', `${this.category}`);
        } else {
            const activeTab = this.tabs.find((tab: HTMLElement) => {
                return tab.classList.contains(ACTIVE_CLASS);
            });
            const category = activeTab?.dataset['tab'] || null;

            form.append('category', `${category}`);
        }

        checked.forEach((input: HTMLInputElement) => {
            form.append('period[]', `${input.value}`);
        });

        return form;
    }
}

export default NewsList;
